/**
 * Impersonation token utilities — BOLT-025.
 *
 * stashSuperAdminToken / restoreSuperAdminToken — persist the super-admin's
 * pre-impersonation auth state in sessionStorage so it can be restored when
 * the session ends. sessionStorage is tab-scoped; the stash clears on tab close.
 *
 * decodeJwtPayload — base64-decode a JWT payload without verifying the signature.
 * Safe for reading claims client-side — the API already validated the signature.
 */

import { type UserRole } from '@/store/auth.js';

const STASH_KEY = '97d.superAdminToken';

export interface SuperAdminStash {
  accessToken: string;
  orgId: string;
  userId: string;
  role: UserRole;
}

/**
 * Saves the super-admin's current auth state to sessionStorage before
 * swapping to an impersonation token.
 */
export function stashSuperAdminToken(state: SuperAdminStash): void {
  sessionStorage.setItem(STASH_KEY, JSON.stringify(state));
}

/**
 * Restores and removes the stashed super-admin auth state.
 * Returns null if no stash exists (e.g. impersonation was started in another tab).
 */
export function restoreSuperAdminToken(): SuperAdminStash | null {
  const raw = sessionStorage.getItem(STASH_KEY);
  if (!raw) return null;
  sessionStorage.removeItem(STASH_KEY);
  try {
    return JSON.parse(raw) as SuperAdminStash;
  } catch {
    return null;
  }
}

/**
 * Clears the stashed super-admin auth state from sessionStorage.
 * Call this when the session expires or the user logs out to ensure a stale
 * credential cannot be picked up by a subsequent restoreSuperAdminToken() call.
 */
export function clearSuperAdminStash(): void {
  sessionStorage.removeItem(STASH_KEY);
}

const ENDED_FLAG_KEY = '97d.impersonationEnded';

/**
 * Marks that this tab's impersonation ended out from under it — BOLT-403.
 *
 * Set just before the full-page replace that restores the super-admin
 * identity (silent refresh discovered the session was ended or hard-capped).
 * sessionStorage survives the reload but stays tab-scoped, and AuthBootstrap
 * consumes it on the next mount to raise a visible toast — the user must
 * never discover the identity switch by reading a page header.
 */
export function markImpersonationEnded(targetEmail: string | null): void {
  sessionStorage.setItem(ENDED_FLAG_KEY, targetEmail ?? '');
}

/**
 * One-shot read of the impersonation-ended marker. Returns null when the tab
 * did not just come back from an out-from-under session end; otherwise the
 * impersonated email ('' when unknown) and clears the flag.
 */
export function consumeImpersonationEnded(): string | null {
  const value = sessionStorage.getItem(ENDED_FLAG_KEY);
  if (value === null) return null;
  sessionStorage.removeItem(ENDED_FLAG_KEY);
  return value;
}

/**
 * Decodes a JWT payload segment without verifying the signature.
 *
 * @param token - raw JWT string (three base64url segments separated by `.`)
 * @returns decoded payload, or null if the token is malformed
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function decodeJwtPayload<T extends Record<string, any>>(token: string): T | null {
  try {
    const parts = token.split('.');
    const segment = parts[1];
    if (parts.length !== 3 || !segment) return null;
    // base64url → standard base64 (re-pad to multiple of 4)
    const padded =
      segment.replace(/-/g, '+').replace(/_/g, '/') +
      '==='.slice(0, (4 - (segment.length % 4)) % 4);
    return JSON.parse(atob(padded)) as T;
  } catch {
    return null;
  }
}
